import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FileText, Send, Sparkles, UploadCloud } from 'lucide-react';
import CaseDocuments from './CaseDocuments';
import CaseDiscovery from './CaseDiscovery';
import DocumentIntelPanel from './DocumentIntelPanel';

// Tab-consolidation: Documents is the single home for case files. Discovery
// delivery (D1 manifest/OTP/verify) becomes the "Produce & Deliver" sub-view,
// acting on the discoverable/exhibit flags set in Files; Doc Intel is a side
// sub-view. The public /discovery/download page is unaffected.
//
// Drag-and-drop: the WHOLE tab is one drop zone (a file dropped anywhere —
// including the sub-view bar or the Produce/Doc-Intel views — routes to Files
// and into the existing batch-upload path via dropSignal; no second upload code
// path). preventDefault everywhere kills the browser-default navigate-to-file.
const SUBVIEWS = [
  { id: 'files', label: 'Files', icon: FileText },
  { id: 'produce', label: 'Produce & Deliver', icon: Send },
  { id: 'intel', label: 'Doc Intel', icon: Sparkles },
];

const CaseDocumentsTab = ({ caseId, caseName }) => {
  const [view, setView] = useState('files');
  const [dragActive, setDragActive] = useState(false);
  const [dropSignal, setDropSignal] = useState(null);
  const dragDepth = useRef(0);        // enter/leave counter — survives child flicker

  const clearDrag = useCallback(() => {
    dragDepth.current = 0;
    setDragActive(false);
  }, []);

  // Esc clears the drag-over affordance.
  useEffect(() => {
    if (!dragActive) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') clearDrag(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [dragActive, clearDrag]);

  const onDragEnter = (e) => {
    if (!e.dataTransfer?.types?.includes('Files')) return;  // ignore non-file drags
    e.preventDefault();
    dragDepth.current += 1;
    setDragActive(true);
  };
  const onDragOver = (e) => {
    if (!e.dataTransfer?.types?.includes('Files')) return;
    e.preventDefault();   // REQUIRED for a drop to fire + to block browser navigate
    e.dataTransfer.dropEffect = 'copy';
  };
  const onDragLeave = (e) => {
    e.preventDefault();
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDragActive(false);
  };
  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    clearDrag();
    // Snapshot to an array — the dataTransfer FileList is cleared after the event.
    const files = e.dataTransfer?.files ? Array.from(e.dataTransfer.files) : [];
    if (!files.length) return;
    // A drop on any sub-view redirects to Files, then uploads there.
    if (view !== 'files') setView('files');
    setDropSignal({ files, id: Date.now() + '-' + files.length });
  };

  return (
    <div
      className="relative"
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      {dragActive && (
        <div className="absolute inset-0 z-40 bg-cyan-500/10 border-2 border-dashed border-cyan-400 rounded-xl flex items-center justify-center pointer-events-none">
          <div className="text-center text-cyan-300">
            <UploadCloud size={40} className="mx-auto mb-2" />
            <p className="font-semibold">Drop files to upload to this case</p>
            {view !== 'files' && <p className="text-xs text-cyan-400/80 mt-1">(will open the Files view)</p>}
          </div>
        </div>
      )}

      <div className="flex gap-2 px-6 pt-6">
        {SUBVIEWS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setView(id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors ${
              view === id
                ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white'
                : 'bg-virgo-navy-600 text-virgo-teal-300 border border-virgo-teal-700/50 hover:bg-virgo-navy-500'
            }`}
          >
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      {view === 'files' && <CaseDocuments caseId={caseId} dropSignal={dropSignal} />}
      {view === 'produce' && <CaseDiscovery caseId={caseId} caseName={caseName} />}
      {view === 'intel' && <DocumentIntelPanel caseId={caseId} />}
    </div>
  );
};

export default CaseDocumentsTab;
